import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@fhs/ui';
import { useUIContext } from '@fhs-legacy/frontend/src/state/ui';
import { CartEntryType } from '@fhs-legacy/frontend/src/utils/cart';
import {
  CART_ITEM_DESCRIPTION,
  SEE_LESS_BUTTON,
  SEE_MORE_BUTTON,
} from '@fhs-legacy/frontend/src/utils/test-ids';
import { ICartEntry } from '@rbi-ctg/menu';

export default function ItemModifierList({ modifiers }: { modifiers: ICartEntry[] }) {
  const { formatCurrencyForLocale } = useUIContext();

  const [isExpanded, setIsExpanded] = useState(false);

  const modifierList = composeModifierList(modifiers, formatCurrencyForLocale);

  const DEFAULT_MODIFIERS_SHOWN = 2;
  // All modifiers show when expanded or 2 modifiers shown when not expanded
  const visibleModifiers = isExpanded ? modifierList.length : DEFAULT_MODIFIERS_SHOWN;
  const visibleModifiersList = modifierList.slice(0, visibleModifiers);

  return (
    <View testID={CART_ITEM_DESCRIPTION} style={styles.modifierContainer}>
      {visibleModifiersList.map(modifier => {
        return (
          <Text.Ui size="md" key={modifier}>
            {modifier}
          </Text.Ui>
        );
      })}

      {modifierList?.length > DEFAULT_MODIFIERS_SHOWN && (
        <Pressable
          testID={isExpanded ? SEE_LESS_BUTTON : SEE_MORE_BUTTON}
          onPress={(e) => {
            // Stop event propagation to prevent triggering the parent edit button
            e?.stopPropagation?.();
            setIsExpanded(currentExpand => !currentExpand);
          }}
          accessibilityRole="button"
          accessibilityLabel={isExpanded ? 'See less item details' : 'See more item details'}
          accessibilityHint={
            isExpanded
              ? 'Collapses the item details list'
              : `Expands to show all ${modifierList.length} item details`
          }
          accessibilityState={{ expanded: isExpanded }}
        >
          <Text.Ui size="sm" weight="bold" style={styles.seeMoreButton}>
            See {isExpanded ? 'Less' : 'More'}
          </Text.Ui>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  seeMoreButton: {
    paddingVertical: 8,
    textDecorationLine: 'underline',
    width: 75,
  },
  modifierContainer: {
    paddingVertical: 8,
    width: '125%', // prevents unnecessary wrapping
  },
});

function composeModifierList(
  cartEntryChildren: ICartEntry[],
  formatCurrencyForLocale: (amount: number) => string
) {
  /* Recursively iterates through the children of the main cart entry item
  if the type is not an itemOption or comboSlot to avoid duplicated modifiers
  and main entry names in the see more list. Combo slot with price are added*/

  const displayNameSet = new Set<string>();

  const iterateThroughChildren = (entryChildren: ICartEntry[]) => {
    for (const entryChild of entryChildren) {
      const isIgnoredCartEntryType =
        entryChild.type === CartEntryType.itemOption || entryChild.type === CartEntryType.comboSlot;

      if (!isIgnoredCartEntryType) {
        const itemName = entryChild.name;

        const quantity = entryChild?.quantity > 1 ? `${entryChild.quantity} ` : '';
        const price = entryChild.price || 0;

        const shouldInclude = entryChild.type === CartEntryType.itemOptionModifier || price > 0;

        if (shouldInclude) {
          const label =
            price > 0
              ? `${quantity}${itemName} + ${formatCurrencyForLocale(price)}`
              : `${quantity}${itemName}`;

          if (!displayNameSet.has(label)) {
            displayNameSet.add(label);
          }
        }
      }

      if (entryChild.children?.length) {
        iterateThroughChildren(entryChild.children);
      }
    }
  };

  iterateThroughChildren(cartEntryChildren);

  return Array.from(displayNameSet);
}
